import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { performanceApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { BarChart3, CheckCircle, Clock, AlertTriangle, Palette, Send, Users, TrendingUp } from 'lucide-react';
import { cn } from '../lib/utils';

const PerformancePage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try { const r = await performanceApi.getEmployees(); setEmployees(r.data); } catch { toast.error('Error al cargar desempeño'); } finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <DashboardLayout title="Desempeño"><div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" /></div></DashboardLayout>;

  // Global stats
  const totalTasks = employees.reduce((a, e) => a + e.tasks_assigned, 0);
  const totalCompleted = employees.reduce((a, e) => a + e.tasks_completed, 0);
  const totalInProgress = employees.reduce((a, e) => a + e.tasks_in_progress, 0);
  const totalOverdue = employees.reduce((a, e) => a + (e.tasks_overdue || 0), 0);
  const globalRate = totalTasks > 0 ? ((totalCompleted / totalTasks) * 100).toFixed(1) : 0;

  // Sort by completion rate
  const sorted = [...employees].sort((a, b) => b.completion_rate - a.completion_rate);

  return (
    <DashboardLayout title="Desempeño">
      <div className="space-y-6" data-testid="performance-page">
        {/* Global KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Tareas', value: totalTasks, icon: BarChart3, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            { label: 'Completadas', value: totalCompleted, icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10' },
            { label: 'En Progreso', value: totalInProgress, icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
            { label: 'Vencidas', value: totalOverdue, icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
            { label: 'Tasa Global', value: `${globalRate}%`, icon: TrendingUp, color: 'text-orange-400', bg: 'bg-orange-500/10' },
          ].map((kpi, i) => (
            <Card key={i} className="bg-[#141414] border-white/5" data-testid={`kpi-${i}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={cn('p-2.5 rounded-lg', kpi.bg)}><kpi.icon className={kpi.color} size={20} /></div>
                <div>
                  <p className="text-xs text-zinc-500">{kpi.label}</p>
                  <p className="text-xl font-bold text-white">{kpi.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Employee Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sorted.map((emp, idx) => {
            const rateColor = emp.completion_rate >= 80 ? 'text-green-400' : emp.completion_rate >= 50 ? 'text-amber-400' : 'text-red-400';
            const barColor = emp.completion_rate >= 80 ? 'bg-green-500' : emp.completion_rate >= 50 ? 'bg-amber-500' : 'bg-red-500';
            return (
              <Card key={emp.user_id} className="bg-[#141414] border-white/5 hover:border-white/10 transition-colors" data-testid={`employee-card-${emp.user_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Avatar & Rank */}
                    <div className="relative">
                      <Avatar className="h-14 w-14">
                        <AvatarImage src={emp.picture} />
                        <AvatarFallback className="bg-orange-600 text-white text-lg">{emp.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">#{idx + 1}</span>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <h3 className="text-white font-semibold">{emp.name}</h3>
                          <p className="text-xs text-zinc-500">{emp.position || emp.role}</p>
                        </div>
                        <div className="text-right">
                          <p className={cn('text-2xl font-bold', rateColor)}>{emp.completion_rate}%</p>
                          <p className="text-[10px] text-zinc-500">completado</p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="w-full h-2 rounded-full bg-[#1A1A1A] mb-3 overflow-hidden">
                        <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${Math.min(emp.completion_rate, 100)}%` }} />
                      </div>

                      {/* Metric chips */}
                      <div className="grid grid-cols-4 gap-2">
                        <div className="text-center p-2 rounded-lg bg-[#1A1A1A]">
                          <p className="text-sm font-bold text-white">{emp.tasks_assigned}</p>
                          <p className="text-[10px] text-zinc-500">Asignadas</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-[#1A1A1A]">
                          <p className="text-sm font-bold text-green-400">{emp.tasks_completed}</p>
                          <p className="text-[10px] text-zinc-500">Completadas</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-[#1A1A1A]">
                          <p className="text-sm font-bold text-amber-400">{emp.tasks_in_progress}</p>
                          <p className="text-[10px] text-zinc-500">En Progreso</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-[#1A1A1A]">
                          <p className="text-sm font-bold text-red-400">{emp.tasks_overdue || 0}</p>
                          <p className="text-[10px] text-zinc-500">Vencidas</p>
                        </div>
                      </div>

                      {/* Additional metrics */}
                      <div className="flex gap-3 mt-2 text-xs text-zinc-500">
                        {emp.content_created > 0 && <span className="flex items-center gap-1"><Palette size={10} className="text-purple-400" />{emp.content_created} contenidos</span>}
                        {emp.content_designed > 0 && <span className="flex items-center gap-1"><Palette size={10} className="text-blue-400" />{emp.content_designed} diseños</span>}
                        {emp.content_published > 0 && <span className="flex items-center gap-1"><Send size={10} className="text-green-400" />{emp.content_published} publicados</span>}
                        {emp.work_orders_handled > 0 && <span className="flex items-center gap-1"><Users size={10} className="text-orange-400" />{emp.work_orders_handled} ordenes</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {employees.length === 0 && (
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BarChart3 className="text-zinc-600 mb-4" size={48} />
              <p className="text-zinc-400">No hay datos de desempeño disponibles</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PerformancePage;
